import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Eyebrow, Reveal, Section } from "@/components/Primitives";
import { CtaBanner } from "@/components/Blocks";

const CHAPTERS = [
    {
        n: "01",
        title: "We start with the process",
        body: "Before a line of code is written, we map how your team actually works today — the real path a piece of work takes, the handoffs, the re-keying, the waiting. The automation opportunity is defined against that map, not against a demo.",
    },
    {
        n: "02",
        title: "We build for your environment",
        body: "We design and fine-tune smaller, purpose-trained models around your domain and data — lighter, faster, and more predictable at scale, and deployable inside your own private environment. Your data stays inside your control.",
    },
    {
        n: "03",
        title: "One team carries it through",
        body: "The people who scope an engagement are the people who design, build, and deploy it. There is no handoff to a separate delivery bench, and no gap where accountability leaks out.",
    },
    {
        n: "04",
        title: "We measure before we promise",
        body: "We do not publish borrowed percentages. We baseline your volumes, handling time, and cost per interaction in discovery, so the return you plan for is your return — not an industry average.",
    },
];

export default function About() {
    return (
        <>
            <Seo
                title="About"
                description="Phoebus AI Solutions is an India-headquartered AI engineering firm with regional delivery experience and named GCC clients."
                path="/about"
            />

            <Section className="pt-16 md:pt-24 pb-10">
                <Reveal>
                    <Eyebrow>About Phoebus AI</Eyebrow>
                    <h1 className="mt-6 font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
                        An AI engineering firm, not a reseller
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-pa-ink-500 max-w-3xl leading-relaxed">
                        Phoebus AI Solutions is India-headquartered, with regional
                        delivery experience and named clients across the GCC —
                        including Eagle Hills in Abu Dhabi and Qatar Post. We
                        build systems that live inside our clients' businesses.
                    </p>
                </Reveal>
            </Section>

            <Section className="pt-0">
                <div className="grid gap-12 md:grid-cols-[280px_1fr] md:gap-16">
                    <div className="md:sticky md:top-28 md:self-start">
                        <Reveal>
                            <Eyebrow>What we hold to</Eyebrow>
                            <p className="mt-4 font-display text-2xl font-bold leading-snug">
                                Four things that do not change from one
                                engagement to the next.
                            </p>
                        </Reveal>
                    </div>
                    <div className="space-y-px bg-pa-line border border-pa-line cut-tr-lg overflow-hidden">
                        {CHAPTERS.map((ch, i) => (
                            <Reveal key={ch.n} delay={i * 0.05}>
                                <div className="bg-white p-8 md:p-10">
                                    <span className="font-mono text-sm text-pa-blue-600">
                                        {ch.n}
                                    </span>
                                    <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold">
                                        {ch.title}
                                    </h2>
                                    <p className="mt-3 text-pa-ink-500 leading-relaxed max-w-2xl">
                                        {ch.body}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </Section>

            <CtaBanner />
        </>
    );
}
